import { useCallback, useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';

import { IconLink, theme, useResponsive, useSafeRouter } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { Navbar } from '../../../../../components';
import {
  CONTENT_WITH_BG_COLOR_STYLES,
  CONTENT_WITHOUT_BG_STYLES,
  SCREEN_HEADER_OPTIONS,
  type ScreenOptions,
  type ScreenOptionsMap,
} from '../../../../constants';
import { useRouteTitles } from '../../../messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Screen options for the modals stack.
 */
export const useModalStackOptions = () => {
  const { match } = useResponsive();

  const titles = useRouteTitles();
  const isStripeSdkEnabled = useFeatureFlag('CELS-4707-scan-to-pay-stripe-sdk-enabled'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const stackOptions = useMemo(() => {
    return {
      ...SCREEN_HEADER_OPTIONS,
      headerShown: match([false, true]),
      header: match([undefined, () => <Navbar />]),
      headerLeft: () => <NavBackButton />,
      presentation: match(['card', 'transparentModal']),
      contentStyle: CONTENT_WITH_BG_COLOR_STYLES,
    } satisfies ScreenOptions;
  }, [match]);

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(() => {
    return {
      //
      // ─── Main Stack Options (applied to all screens) ─────────────────────

      Stack: stackOptions,

      // ─── Individual Screen Options ───────────────────────────────────────

      Bag: { headerShown: false, customDocumentTitle: titles.bagCustomDocumentTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Reorder:
        { headerShown: false, title: titles.reorderTitle, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      ReorderSubScreen:
        { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      RequestData: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      DeliveryOrderInFlight: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Feedback: { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      OrderProduct: { headerShown: match([true, false]), contentStyle: CONTENT_WITHOUT_BG_STYLES }, // prettier-ignore
      Chat: { headerShown: false, contentStyle: CONTENT_WITHOUT_BG_STYLES },
      PersonalData: { title: titles.personalDataTitle },
      AddGiftCard: { title: titles.addGiftCardTitle, headerShown: true },
      RedeemGiftCard: { title: titles.redeemGiftCardTitle, headerShown: true },
      Upsell: { contentStyle: CONTENT_WITHOUT_BG_STYLES, headerShown: false },
      Payments: isStripeSdkEnabled
        ? {
            presentation: Platform.select({
              ios: 'modal',
              default: 'transparentModal',
            }),
            headerShown: false,
            contentStyle: CONTENT_WITHOUT_BG_STYLES,
          }
        : {},
      OrderStatus: {
        headerShown: true,
        customDocumentTitle: titles.orderStatusCustomDocumentTitle,
      },
      ForgotToScan: {
        customDocumentTitle: titles.forgotToScanCustomDocumentTitle,
      },
      Receipt: {
        headerShown: true,
        customDocumentTitle: titles.receiptCustomDocumentTitle,
      },
      ClaimReceipt: {
        headerShown: true,
        customDocumentTitle: titles.receiptClaimCustomDocumentTitle,
      },
      WithHeader: { headerShown: true },
    } satisfies ScreenOptionsMap;
  }, [
    isStripeSdkEnabled,
    match,
    stackOptions,
    titles.addGiftCardTitle,
    titles.bagCustomDocumentTitle,
    titles.forgotToScanCustomDocumentTitle,
    titles.orderStatusCustomDocumentTitle,
    titles.personalDataTitle,
    titles.redeemGiftCardTitle,
    titles.reorderTitle,
    titles.receiptCustomDocumentTitle,
    titles.receiptClaimCustomDocumentTitle,
  ]);
};

// ─────────────────────────────────────────────────────────────────────────────

const NavBackButton = () => {
  const router = useSafeRouter();

  const handlePress = useCallback(() => {
    if (router?.canGoBack()) {
      router.back();
      return;
    }

    router?.dismissTo('/account/menu');
  }, [router]);

  return (
    <IconLink
      name="IconArrowLeft"
      style={styles.backButton}
      color={theme.colors.GREEN_1}
      accessibilityLabel="Back"
      onPress={handlePress}
    />
  );
};

// ─── Constants ──────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  backButton: {
    marginLeft: Platform.select({
      web: theme.spacing['4'],
      default: 0,
    }),
  },
});
